import { useMemo } from "react";
import {
  CatalogPriceZoneServiceProvider,
  CatalogPriceZoneSP,
  ManualProductPrices,
  QueryParams,
  UrbanCenterWithZone,
} from "types";
import useRequest from "../useRequest";
import {
  DocumentProductLinePrice,
  Price,
  PriceFamily,
  PriceOption,
  Product,
} from "models";
import {
  PRICE_TYPE_LOGICS,
  ProductTarifType,
  TARIF_TYPE,
} from "utils/constant";
import { boService } from "services";

export type InputProductType = {
  productId?: number;
  priceFamilyId?: number;
  quantity?: number;
  documentProductLineId?: number;
  DocumentProductLinePrices?: DocumentProductLinePrice[];
  SPDocumentProductLinePrices?: DocumentProductLinePrice[];
  unitPrice?: string | number;
};

export type MoreProductType = {
  serviceProviderId?: number;
  productId?: number;
};

type CatalogPriceZoneServiceProviderQueryParamsType = QueryParams & {
  qboServiceProviderId?: number | null;
  serviceProviderZoneIds?: UrbanCenterWithZone[];
  products?: InputProductType[];
  productTypeIds?: number[];
  siteAddressFull?: string;
  contactId?: number;
};

export const useCatalogPriceZoneServiceProviderQuery = (
  query?: CatalogPriceZoneServiceProviderQueryParamsType
) => {
  return useMemo(() => {
    return [{ ...query }];
  }, []);
};

export type CatalogPriceZoneSPPriceOption = PriceOption & {
  priceValue: string;
  catalogPriceValue?: string | number;
  isManualPrice?: boolean;
  wasInitiallyManual?: boolean;
  orderCreatedAt?: string | null;
};

export type CatalogPriceZoneSPPrice = Price & {
  priceValue: string;
  catalogPriceValue?: string | number;
  isManualPrice?: boolean;
  wasInitiallyManual?: boolean;
  priceOptions?: CatalogPriceZoneSPPriceOption[];
  orderCreatedAt?: string | null;
};

export type CatalogPriceZoneSPPriceFamily = PriceFamily & {
  serviceProviderId: number;
  priceValue: string;
  catalogPriceValue?: string | number;
  isManualPrice?: boolean;
  wasInitiallyManual?: boolean;
  comment: string;
  prices?: CatalogPriceZoneSPPrice[];
  orderCreatedAt?: string | null;
};


export type CatalogPriceZoneProduct = {
  productId: number;
  productName: string;
  priceValue: string;
  isTransport: boolean;
  productTypeId: number;
  priceFamilies: CatalogPriceZoneSPPriceFamily[];
}

export type CombineCatalogPriceLineZoneServiceProvider = {
  serviceProviderId: number;
  serviceProviderName: string;
  formattedAddress: string;
  isValid?: boolean;
  selectedPriceFamilyId?: number | null;
  products: {
    uuid?: string;
    productId?: number;
    productName?: string;
    priceFamilies?: CatalogPriceZoneSPPriceFamily[];
    priceValue: string | number;
    catalogPriceValue?: string | number;
    isManualPrice?: boolean;
    wasInitiallyManual?: boolean;
    selectedPriceFamilyId?: number | null;
    quantity?: number;
    comment?: string | null;
    isTransport?: boolean;
    productTypeInterventionKey?: string;
    priceMargin?: string;
    documentProductLineId?: number;
    documentProductLinePrices?: DocumentProductLinePrice[];
    orderCreatedAt?: string | null;
  }[];
  zones: {
    zoneId: number;
    zoneName: string;
    distanceByCar?: string;
  }[];
};

/**
 * Hook to fetch catalog price zone service providers based on query parameters.
 *
 * @param query - The query parameters for fetching catalog price zone service providers.
 * @returns A request object with the fetched data.
 */
export const useCatalogPriceZoneServiceProvider = (
  query: CatalogPriceZoneServiceProviderQueryParamsType
) => {
  /**
   * Fetches catalog price zone service providers and associated data.
   *
   * @param options - Optional query parameters to merge with the original query.
   * @returns A promise resolving to an array of combined catalog price line zone service provider data.
   */
  const action = async (
    options?: CatalogPriceZoneServiceProviderQueryParamsType
  ) => {
    try {
      const mergedQuery = { ...query, ...options };
      const {
        serviceProviderZoneIds = [],
        products: inputProducts = [],
        qboServiceProviderId,
      } = mergedQuery;

      const selectedProductIds = inputProducts.map((p) => ({
        productId: p.productId,
        priceFamilyId: p.priceFamilyId,
      }));

      // CASE: SP outside the zone
      if (
        (!serviceProviderZoneIds.length || !inputProducts.length) &&
        qboServiceProviderId
      ) {
        const productIds = new Set<number>();

        // Add productIds from inputProducts
        if (inputProducts && inputProducts.length > 0) {
          inputProducts.forEach((p) => {
            if (p.productId != null) productIds.add(p.productId);
          });
        }

        return await handleLoadDefaultDataForSpOutsideZone(
          inputProducts,
          productIds,
          qboServiceProviderId,
          mergedQuery.contactId,
          mergedQuery.siteAddressFull
        );
      }

      // Fetching to get the price for zone service providers
      const zoneIdsArr = serviceProviderZoneIds.map(({ zoneId }) => zoneId);

      // First, fetch the products to get their productTypeIds
      const productIdsSet = new Set<number>();
      inputProducts.forEach(p => p.productId && productIdsSet.add(p.productId));

      const { data: productsDataFromAPI } = await boService.getQBOProductsByIds({
        "id[]": JSON.stringify([...productIdsSet]),
        limit: "unlimited",
        include: "ProductType.ProductTypeIntervention",
      });

      // Fetch additional products based on generated names
      const volumes = [
        ...(productsDataFromAPI?.rows || [])
          .filter((product) => product.name && / - .+$/.test(product.name))
          .map((product) => product.name.match(/ - (.+)$/)?.[1] || ''),
        ...inputProducts
          .map((p) => {
            const product = productsDataFromAPI?.rows?.find(prod => prod.id === p.productId);
            return product?.name?.match(/ - (.+)$/)?.[1] || '';
          })
          .filter((volume) => volume),
      ].filter((volume) => volume);

      const productNames = [
        ...volumes.map((volume) => `Transport Benne : Pose - ${volume}`),
        ...volumes.flatMap((volume) => [
          `Transport Benne : Enlèvement - ${volume}`,
          `Transport Benne : Rotation - ${volume}`,
        ]),
        'Location Benne : Mensuelle',
      ].filter((name): name is string => !!name);

      // Fetch additional products
      let allProducts = [...(productsDataFromAPI?.rows || [])];
      if (productNames.length > 0) {
        try {
          const { data: additionalProductsData } = await boService.getQBOProducts({
            "name[]": JSON.stringify(productNames),
            include: "ProductType.ProductTypeIntervention",
            limit: "unlimited",
          });

          if (additionalProductsData?.rows?.length) {
            allProducts = [...allProducts, ...additionalProductsData.rows];
          }
        } catch (error) {
          console.error('Error fetching additional products:', error);
        }
      }

      // Extract productTypeIds from all products (initial + additional)
      const productTypeIdsFromProducts = allProducts
        ?.filter((p) => p.productTypeId)
        .map((p) => p.productTypeId) || [];
      const uniqueProductTypeIds = [...new Set(productTypeIdsFromProducts)];

      const requestPayload: {
        serviceProviderZoneIdFulls: UrbanCenterWithZone[];
        serviceProviderZoneIds: number[];
        products: { productId?: number; priceFamilyId?: number }[];
        productTypeIds?: number[];
        siteAddressFull?: string;
        contactId?: number;
      } = {
        serviceProviderZoneIdFulls: serviceProviderZoneIds,
        serviceProviderZoneIds: zoneIdsArr,
        products: [...selectedProductIds],
        ...(mergedQuery.siteAddressFull
          ? { siteAddressFull: mergedQuery.siteAddressFull }
          : {}),
        ...(mergedQuery.contactId ? { contactId: mergedQuery.contactId } : {}),
      };

      if (query?.productTypeIds?.length) {
        requestPayload.productTypeIds = query.productTypeIds;
      } else if (uniqueProductTypeIds.length > 0) {
        requestPayload.productTypeIds = uniqueProductTypeIds;
      }
      let initialProductResults: Array<{ id: number; type: ProductTarifType }> = [];
      let initialCatalogEntries: CatalogPriceZoneServiceProvider[] = [];
      let initialManualProductPrices: ManualProductPrices[] = [];

      const response = await boService.getQBOCatalogPriceZoneServiceProviders(requestPayload);

      // If we have data, use it, otherwise create default data
      if (response.data?.serviceProviderCatalogPriceWithPrice?.length > 0) {
        initialProductResults = response.data.products || [];
        initialCatalogEntries = response.data.serviceProviderCatalogPriceWithPrice;
        initialManualProductPrices = response.data.manualProductPrices || [];
      } else if (query.qboServiceProviderId) {
        const productIds = new Set<number>();
        inputProducts.forEach(p => p.productId && productIds.add(p.productId));

        // Only proceed if we have product IDs
        if (productIds.size > 0) {
          // Fetch data of products and price families
          const { productsData, priceFamiliesData } = await fetchProductsAndFamilies(productIds);

          // Create default product results
          initialProductResults = productsData.rows.map(product => ({
            id: product.id,
            type: TARIF_TYPE.FORFAIT as ProductTarifType,
            productTypeId: product.productTypeId
          }));

          // Create default catalog entries for each zone
          if (query.serviceProviderZoneIds?.length) {
            initialCatalogEntries = query.serviceProviderZoneIds.map(zone => {

              const catalogPrice: CatalogPriceZoneServiceProvider & { products: CatalogPriceZoneProduct[] } = {
                serviceProviderId: Number(query.qboServiceProviderId),
                formattedAddress: zone.formattedAddress || '',
                zoneId: zone.zoneId,
                zoneName: zone.zoneName,
                productTypeId: 0,
                priceValue: '0',
                isValid: true,
                distanceByCar: '0',
                priceType: TARIF_TYPE.FORFAIT as ProductTarifType,
                typeValid: 'VALID',
                comment: '',
                products: productsData.rows.map(product => ({
                  productId: product.id,
                  productName: product.name || '',
                  priceValue: '0',
                  isTransport: product.isTransport || false,
                  productTypeId: product.productTypeId || 0,
                  priceFamilies: mappingCatalogPriceZoneSPForPriceFamilies({
                    serviceProviderId: Number(query.qboServiceProviderId),
                    productId: product.id,
                    priceFamilies: priceFamiliesData.filter(pf => pf.productTypeId === product.productTypeId),
                    manualProductPrices: []
                  })
                }))
              };
              return catalogPrice;
            });
          }
        }
      }

      // Create sets to track unique productIds, zoneIds, and serviceProviderIds
      const productIds = new Set<number>();
      const zoneIds = new Set<number>();
      const serviceProviderIds = new Set<number>();

      // Add productIds from productMap array
      if (initialProductResults && initialProductResults.length > 0) {
        initialProductResults.forEach((p) => {
          if (p.id != null) productIds.add(p.id);
        });
      }

      // Add productIds from inputProducts
      if (inputProducts && inputProducts.length > 0) {
        inputProducts.forEach((p) => {
          if (p.productId != null && !productIds.has(p.productId))
            productIds.add(p.productId);
        });
      }

      let productResults = [...initialProductResults];
      let catalogEntries = [...initialCatalogEntries];
      let manualProductPrices = [...initialManualProductPrices];

      // Add zoneIds and serviceProviderIds from catalog entries
      catalogEntries.forEach(({ zoneId, serviceProviderId }) => {
        if (zoneId != null) zoneIds.add(zoneId);
        if (serviceProviderId != null)
          serviceProviderIds.add(serviceProviderId);
      });

      // Fetch data for products, zones, and service providers
      const [{ data: initialProductsData }, { data: providersData }] =
        await Promise.all([
          boService.getQBOProductsByIds({
            "id[]": JSON.stringify([...productIds]),
            limit: "unlimited",
            include: "ProductType.ProductTypeIntervention",
          }),
          boService.getQBOServiceProvidersByIds({
            "id[]": JSON.stringify([...serviceProviderIds]),
            limit: "unlimited",
          }),
        ]);

      // Extract volumes from initialProductsData and inputProducts (using initialProductsData for name)
      // const volumes = [
      //   ...initialProductsData.rows
      //     .filter((product) => product.name && / - .+$/.test(product.name))
      //     .map((product) => product.name.match(/ - (.+)$/)?.[1] || ""),
      //   ...inputProducts
      //     .map((p) => {
      //       const product = initialProductsData.rows.find(
      //         (prod) => prod.id === p.productId
      //       );
      //       return product?.name?.match(/ - (.+)$/)?.[1] || "";
      //     })
      //     .filter((volume) => volume),
      // ].filter((volume) => volume);

      // Generate product names for all products (Pose, Enlèvement, and Rotation)
      // const productNames = [
      //   ...volumes.map((volume) => `Transport Benne : Pose - ${volume}`),
      //   ...volumes.flatMap((volume) => [
      //     `Transport Benne : Enlèvement - ${volume}`,
      //     `Transport Benne : Rotation - ${volume}`,
      //   ]),
      // ].filter((name): name is string => !!name);

      let productsData = {
        rows: [...(initialProductsData.rows || [])],
      };

      if (productsData.rows.length > 0) {
        try {
          const { data: productsDataNew } = await boService.getQBOProducts({
            "name[]": JSON.stringify(productNames),
            include: "ProductType.ProductTypeUnit|ProductType.ProductTypeIntervention",
            limit: "unlimited",
          });

          if (productsDataNew?.rows?.length) {
            productsData.rows = [...productsData.rows, ...(productsDataNew.rows || [])];

            const newProductIds = productsDataNew.rows.map((p) => ({
              productId: p.id,
            }));

            const { data: newData } = await boService.getQBOCatalogPriceZoneServiceProviders({
              serviceProviderZoneIdFulls: serviceProviderZoneIds,
              serviceProviderZoneIds: zoneIdsArr,
              products: newProductIds,
              ...(uniqueProductTypeIds?.length ? { productTypeIds: uniqueProductTypeIds } : {}),
              ...(mergedQuery.siteAddressFull ? { siteAddressFull: mergedQuery.siteAddressFull } : {}),
              ...(mergedQuery.contactId ? { contactId: mergedQuery.contactId } : {}),
            });

            if (newData) {
              const {
                products: newProductResults = [],
                serviceProviderCatalogPriceWithPrice: newCatalogEntries = [],
                manualProductPrices: newManualProductPrices = [],
              } = newData;

              productResults = [...productResults];
              catalogEntries = [...catalogEntries, ...(newCatalogEntries || [])];
              manualProductPrices = [...manualProductPrices, ...(newManualProductPrices || [])];
            }
          }
        } catch (error) {
          console.error('Error fetching additional products:', error);
        }
      }

      // Add productIds from productsData
      if (productsData.rows && productsData.rows.length > 0) {
        productsData.rows.forEach((p) => {
          if (p.id != null) productIds.add(p.id);
        });
      }

      // Fetch price families
      const productTypeIds = productsData?.rows.map((p) => p.productTypeId);
      const { data: priceFamiliesData } = await boService.getQBOPriceFamilies({
        "productTypeId[]": JSON.stringify(productTypeIds),
        include: "Prices.PriceOptions|Prices.PriceTypeLogic",
        ...(mergedQuery?.contactId ? { contactId: mergedQuery.contactId } : {}),
        ...(mergedQuery?.siteAddressFull
          ? { siteAddressFull: mergedQuery.siteAddressFull }
          : {}),
        ...(mergedQuery?.qboServiceProviderId
          ? { serviceProviderId: mergedQuery.qboServiceProviderId }
          : {}),
        ...(productsData?.rows?.[0]?.id
          ? { productId: productsData.rows[0].id }
          : {}),
      });

      // Create a set to track used keys for input and more products
      const usedInputKeys = new Set<string>();
      // Map inputProducts to productResults
      const productMap = productResults.map((prod) => {
        // Find the first unused matching inputProduct
        const matched = inputProducts.find((item) => {
          const key = `${item.documentProductLineId}-${item.productId}`;
          const isMatch = item.productId === prod.id && !usedInputKeys.has(key);
          const inputPriceFamily = priceFamiliesData?.find(
            (pf) => pf.id === item.priceFamilyId
          );
          // If a match is found, add the key to the set and ignore the case of SEMIFORFAIT - Transform product
          if (
            (isMatch && inputPriceFamily?.key !== TARIF_TYPE.SEMIFORFAIT) ||
            (isMatch &&
              inputPriceFamily?.key === TARIF_TYPE.SEMIFORFAIT &&
              prod.type === TARIF_TYPE.SEMIFORFAIT_NON_TRANSFORM)
          ) {
            usedInputKeys.add(key);
            return true;
          }
          // If no match is found, return false
          return false;
        });
        // If no matching inputProduct is found, return an empty object
        return {
          productId: prod.id,
          priceFamilyId: matched?.priceFamilyId,
          quantity: matched?.quantity ?? 0,
          type: prod.type,
          documentProductLineId: matched?.documentProductLineId ?? 0,
          SPDocumentProductLinePrices: matched?.SPDocumentProductLinePrices ?? [],
        };
      });

      // Preparing the quantity number list for source products by ordering
      const qtyListForProducts = inputProducts
        .map((p) => {
          const priceFamily = priceFamiliesData?.find(
            (pf) => pf.id === p?.priceFamilyId
          );
          const product = productMap?.find(
            (prod) => prod.productId === p?.productId
          );
          // If the price family is semiforfait and the product type is not semiforfait non transform
          if (
            priceFamily?.key === TARIF_TYPE.SEMIFORFAIT &&
            product?.type !== TARIF_TYPE.SEMIFORFAIT_NON_TRANSFORM
          ) {
            // Initialize the quantity number list
            let qtyList: number[] = [
              parseFloat(p?.quantity?.toString() ?? "0") ?? 0,
            ];
            // Get the document product line price option list
            const documentProductLinePriceOptionList =
              p?.DocumentProductLinePrices?.filter(
                (price) => price?.priceId && price?.priceOptionId
              );
            if (
              documentProductLinePriceOptionList &&
              documentProductLinePriceOptionList.length > 0
            ) {
              // For each document product line price option
              documentProductLinePriceOptionList.forEach(
                (documentProductLinePriceOption) => {
                  const price = priceFamily?.Prices?.find(
                    (p) => p.id === documentProductLinePriceOption?.priceId
                  );
                  // If the price type logic is calculate
                  if (
                    price?.PriceTypeLogic?.key === PRICE_TYPE_LOGICS?.calculate
                  ) {
                    const qtyNumber = parseFloat(
                      documentProductLinePriceOption?.value?.toString() ?? "0"
                    );
                    // Add the quantity number to the list
                    qtyList = [...qtyList, qtyNumber];
                  } else {
                    // If the price type logic is not calculate
                    qtyList = [...qtyList, 0];
                  }
                }
              );
              return qtyList;
            }
          }
          // If the price family is not semiforfait
          return [parseFloat(p?.quantity?.toString() ?? "0") ?? 0];
        })
        .flat();

      // Create maps for providers for easy access
      const providerMap = new Map(providersData?.rows.map((sp) => [sp.id, sp]));

      // Group catalog entries
      const groupedArray: {
        serviceProviderId: number;
        group: { zoneIds: Set<number> };
      }[] = [];

      // Create a map to group catalog entries
      const groupedMap = new Map<number, { zoneIds: Set<number> }>();

      // Group entries
      for (const { serviceProviderId, zoneId } of catalogEntries) {
        // Skip if serviceProviderId is null
        if (serviceProviderId == null) continue;

        // Create group if it doesn't exist
        let group = groupedMap.get(serviceProviderId);
        // Create group
        if (!group) {
          group = { zoneIds: new Set<number>() };
          groupedMap.set(serviceProviderId, group);
          groupedArray.push({ serviceProviderId, group });
        }

        // Add zoneId to group
        if (zoneId != null) group.zoneIds.add(zoneId);
      }

      // Get selected products and their selling prices
      const selectedProductsFromOrderClient =
        getSelectedProductsAndSellingPricesFromOrderClient({
          inputProducts,
          productMap,
          productsData: productsData.rows,
          priceFamiliesData,
        });

      // Construct the final result array
      const result: CombineCatalogPriceLineZoneServiceProvider[] =
        await Promise.all(
          groupedArray.map(async ({ serviceProviderId: spId, group }) => {
            const { zoneIds } = group;
            const provider = providerMap.get(spId);

            const serviceProviderName = provider?.name || "";
            const catalogPriceForSP = catalogEntries.find(
              (sp) => Number(sp.serviceProviderId) === spId
            );

            // Map inputProducts to productResults
            let productsList = productMap.map((p, index) => {
              // Find the product
              const product = productsData?.rows.find(
                (prod) => prod.id === p.productId
              );

              // Find the catalog price for the product based on properly price type
              const catalogPriceForSPByPriceType = Object.values(
                catalogEntries.reduce(
                  (acc, entry) => {
                    const key = `${entry.serviceProviderId}_${entry.priceType}`;
                    if (!acc[key]) {
                      acc[key] = {
                        ...entry,
                        general: [...(entry.general || [])],
                        forfait: [...(entry.forfait || [])],
                        semiforfait: [...(entry.semiforfait || [])],
                      };
                    } else {
                      acc[key]?.general?.push(...(entry.general || []));
                      acc[key]?.forfait?.push(...(entry.forfait || []));
                      acc[key]?.semiforfait?.push(...(entry.semiforfait || []));
                    }
                    return acc;
                  },
                  {} as Record<string, CatalogPriceZoneServiceProvider>
                )
              ).find(
                (sp: CatalogPriceZoneServiceProvider) =>
                  Number(sp.serviceProviderId) === spId &&
                  sp.priceType === p.type
              );

              // Get the correct tarif types
              const correctTarifList = getCorrectDataTarifTypes(
                p.type,
                catalogPriceForSPByPriceType
              );

              // Handles for the case - no tarif details
              const catalogPriceForProduct = correctTarifList.find(
                (e) =>
                  e.productId === product?.id &&
                  Number(e.serviceProviderId) === spId
              );

              // Handles for the case - tarif details
              const selectedPriceFamilyId = p?.priceFamilyId;

              // Get the price value and comment
              const catalogIsManual = catalogPriceForProduct?.isManualPrice === true;
              let catalogPriceValue = !catalogIsManual ? (catalogPriceForProduct?.priceValue ?? '') : '';
              let priceValue = catalogPriceValue;
              let comment = catalogPriceForProduct?.comment ?? "";
              let isManualPrice = false;
              let wasInitiallyManual = false;
              let orderCreatedAt: string | null = null;

              // Check for manual price in database
              const manualPriceAtProductLevel = manualProductPrices.find(
                (manual) => manual.productId === p.productId && manual.serviceProviderId === spId,
              );

              if (manualPriceAtProductLevel) {
                // If manual price exists, set the flags immediately
                isManualPrice = true;
                wasInitiallyManual = true;
                if (manualPriceAtProductLevel.updatedAt) {
                  orderCreatedAt = new Date(manualPriceAtProductLevel.updatedAt).toISOString();
                }
              }

              // If no catalog price found, try manualProductPrices
              if (!priceValue || priceValue === "") {
                if (manualPriceAtProductLevel?.price) {
                  priceValue = manualPriceAtProductLevel.price;
                  isManualPrice = true;
                  wasInitiallyManual = true;
                }
              }

              // Find the price families
              const filteredPriceFamilies = priceFamiliesData.filter((pf) => pf.productTypeId === product?.productTypeId);

              // Map product with its price families
              const priceFamilies = mappingCatalogPriceZoneSPForPriceFamilies({
                serviceProviderId: spId,
                productId: p.productId,
                priceFamilies: filteredPriceFamilies,
                catalogPriceZoneSP: catalogPriceForSPByPriceType,
                manualProductPrices,
              });

              // Find the selected price family
              const selectedPriceFamily = priceFamilies.find((pf) => pf.id === selectedPriceFamilyId);

              if (selectedPriceFamily) {
                // Get the price value and comment
                priceValue = selectedPriceFamily?.priceValue ?? "";
                catalogPriceValue = selectedPriceFamily?.catalogPriceValue ? String(selectedPriceFamily.catalogPriceValue) : "";
                comment = selectedPriceFamily?.comment ?? "";
                if (selectedPriceFamily.orderCreatedAt) {
                  orderCreatedAt = selectedPriceFamily.orderCreatedAt;
                }

                // If priceValue is empty, try to get from manualProductPrices
                if (!priceValue || priceValue === "") {
                  const manualPriceAtFamilyLevel = manualProductPrices.find(
                    (manual) =>
                      manual.productId === p.productId &&
                      manual.serviceProviderId &&
                      manual.priceFamilyId === selectedPriceFamilyId,
                  );
                  if (manualPriceAtFamilyLevel) {
                    priceValue = manualPriceAtFamilyLevel.price;
                    if (manualPriceAtFamilyLevel.updatedAt) {
                      orderCreatedAt = new Date(manualPriceAtFamilyLevel.updatedAt).toISOString();
                    }
                  }
                }
              } else {
                const manualPriceAtFamilyLevel = manualProductPrices.find(
                  (manual) =>
                    manual.productId === p.productId &&
                    manual.serviceProviderId === spId &&
                    manual.serviceProviderId &&
                    manual.priceFamilyId === selectedPriceFamilyId,
                );
                if (manualPriceAtFamilyLevel) {
                  priceValue = manualPriceAtFamilyLevel?.price ?? "";
                  isManualPrice = true;
                  wasInitiallyManual = true;
                  if (manualPriceAtFamilyLevel.updatedAt) {
                    orderCreatedAt = new Date(manualPriceAtFamilyLevel.updatedAt).toISOString();
                  }
                }
              }

              if (!orderCreatedAt && catalogPriceForProduct?.orderCreatedAt) {
                orderCreatedAt = catalogPriceForProduct.orderCreatedAt;
              }

              const enlevementProduct =
                /Transport Benne : Enlèvement - .+/.test(product?.name || "");
              const rotationProduct = /Transport Benne : Rotation - .+/.test(
                product?.name || ""
              );
              const selectedProductsFromOrderClientBySP =
                selectedProductsFromOrderClient.find(
                  (clientProduct) =>
                    clientProduct.productId === product?.id &&
                    clientProduct.documentProductLineId ===
                    p.documentProductLineId
                );
              // Map product with its price families
              return {
                productId: product?.id,
                productName: product?.name ?? "",
                priceFamilies,
                priceValue,
                catalogPriceValue,
                isManualPrice,
                wasInitiallyManual,
                selectedPriceFamilyId,
                quantity: product?.isTransport
                  ? enlevementProduct || rotationProduct
                    ? 0
                    : 1
                  : qtyListForProducts[index],
                isTransport: product?.isTransport,
                comment,
                productTypeInterventionKey:
                  product?.ProductType?.ProductTypeIntervention?.key,
                sellingUnitPrice:
                  selectedProductsFromOrderClientBySP?.sellingUnitPrice ?? 0,
                documentProductLineId: p.documentProductLineId,
                documentProductLinePrices: p?.SPDocumentProductLinePrices,
                orderCreatedAt,
              };
            });

            // Filter and add relevant products based on quantity
            // const additionalProducts: typeof productsList = [];
            // productsList.forEach((productItem, index) => {
            //   const quantity = inputProducts[index]?.quantity ?? 1;
            //   const volumeMatch =
            //     productItem.productName?.match(/ - (.+)$/)?.[1] || ""; // Extract volume (e.g., '10m3')
            //   const inputUnitPrice = parseFloat(
            //     inputProducts[index]?.unitPrice?.toString() ?? "0"
            //   );

            //   // Find product details from existing productsData
            //   const productData = productsData.rows.find(
            //     (prod) => prod.id === productItem.productId
            //   );
            //   const isTransport = productData?.isTransport ?? false;
            //   const isBenne =
            //     productData?.ProductType?.ProductTypeIntervention?.key ===
            //     "BENNE";

            //   // Handle 'Pose et enlèvement' products
            //   if (
            //     productItem.productName &&
            //     /Transport Benne : Pose et enlèvement - .+/.test(
            //       productItem.productName
            //     )
            //   ) {
            //     // Add Enlèvement product
            //     const enlevementProduct = productsData.rows.find(
            //       (prod) =>
            //         prod.name ===
            //         `Transport Benne : Enlèvement - ${volumeMatch}`
            //     );
            //     const productCatalogPrices = catalogEntries
            //       .filter(
            //         (sp) =>
            //           Number(sp.serviceProviderId) === spId &&
            //           sp.priceType === "GENERAL"
            //       )
            //       .find(
            //         (generalCatalogPrice) =>
            //           generalCatalogPrice?.general &&
            //           generalCatalogPrice?.general?.find(
            //             (general) => general.productId === enlevementProduct?.id
            //           )
            //       );
            //     const enlevementCatalogPrice =
            //       productCatalogPrices?.general?.find(
            //         (general) => general.productId === enlevementProduct?.id
            //       );
            //     if (enlevementProduct) {
            //       additionalProducts.push({
            //         productId: enlevementProduct.id,
            //         productName: enlevementProduct.name,
            //         priceValue: enlevementCatalogPrice?.priceValue ?? "",
            //         comment: enlevementCatalogPrice?.comment ?? "",
            //         priceFamilies: [],
            //         selectedPriceFamilyId: undefined,
            //         quantity: 0,
            //         isTransport: enlevementProduct.isTransport,
            //         productTypeInterventionKey:
            //           enlevementProduct?.ProductType?.ProductTypeIntervention
            //             ?.key,
            //         sellingUnitPrice: inputUnitPrice,
            //       });
            //     }

            //     // Add rotation for Pose et enlèvement if quantity > 1
            //     if (quantity > 1) {
            //       const rotationProduct = productsData.rows.find(
            //         (prod) =>
            //           prod.name ===
            //           `Transport Benne : Rotation - ${volumeMatch}`
            //       );
            //       const productCatalogPrices = catalogEntries
            //         .filter(
            //           (sp) =>
            //             Number(sp.serviceProviderId) === spId &&
            //             sp.priceType === "GENERAL"
            //         )
            //         .find(
            //           (generalCatalogPrice) =>
            //             generalCatalogPrice?.general &&
            //             generalCatalogPrice?.general?.find(
            //               (general) => general.productId === rotationProduct?.id
            //             )
            //         );
            //       const rotationCatalogPrice =
            //         productCatalogPrices?.general?.find(
            //           (general) => general.productId === rotationProduct?.id
            //         );
            //       if (rotationProduct) {
            //         additionalProducts.push({
            //           productId: rotationProduct.id,
            //           productName: rotationProduct.name,
            //           priceValue: rotationCatalogPrice?.priceValue ?? "",
            //           comment: rotationCatalogPrice?.comment ?? "",
            //           quantity: 0,
            //           priceFamilies: [],
            //           selectedPriceFamilyId: undefined,
            //           isTransport: rotationProduct.isTransport,
            //           productTypeInterventionKey:
            //             rotationProduct?.ProductType?.ProductTypeIntervention
            //               ?.key,
            //           sellingUnitPrice: inputUnitPrice,
            //         });
            //       }
            //     }
            //   }
            //   // Add rotation for BENNE products with quantity > 1
            //   else if (
            //     quantity > 1 &&
            //     isTransport &&
            //     isBenne &&
            //     !/Transport Benne : Pose et enlèvement - .+/.test(
            //       productItem.productName || ""
            //     )
            //   ) {
            //     const rotationProductName = `Transport Benne : Rotation - ${volumeMatch || "default"}`; // Fallback volume
            //     const rotationProduct = productsData.rows.find(
            //       (prod) => prod.name === rotationProductName
            //     );
            //     if (rotationProduct) {
            //       const productCatalogPrices = catalogEntries
            //         .filter(
            //           (sp) =>
            //             Number(sp.serviceProviderId) === spId &&
            //             sp.priceType === "GENERAL"
            //         )
            //         .find(
            //           (generalCatalogPrice) =>
            //             generalCatalogPrice?.general &&
            //             generalCatalogPrice?.general?.find(
            //               (general) => general.productId === rotationProduct?.id
            //             )
            //         );
            //       const rotationCatalogPrice =
            //         productCatalogPrices?.general?.find(
            //           (general) => general.productId === rotationProduct?.id
            //         );
            //       additionalProducts.push({
            //         productId: rotationProduct.id,
            //         productName: rotationProduct.name,
            //         priceValue: rotationCatalogPrice?.priceValue ?? "",
            //         comment: rotationCatalogPrice?.comment ?? "",
            //         quantity: 0,
            //         priceFamilies: [],
            //         selectedPriceFamilyId: undefined,
            //         isTransport: rotationProduct.isTransport,
            //         productTypeInterventionKey:
            //           rotationProduct?.ProductType?.ProductTypeIntervention
            //             ?.key,
            //         sellingUnitPrice: inputUnitPrice,
            //       });
            //     }
            //   }
            // });

            // // Append additional products to the end of the list
            // productsList = [
            //   ...productsList.map((productItem) => {
            //     if (
            //       productItem.productName &&
            //       /Transport Benne : Pose et enlèvement - .+/.test(
            //         productItem.productName
            //       )
            //     ) {
            //       const volume =
            //         productItem.productName.match(
            //           /Transport Benne : Pose et enlèvement - (.+)/
            //         )?.[1] || "";
            //       const poseProduct = productsData.rows.find(
            //         (prod) => prod.name === `Transport Benne : Pose - ${volume}`
            //       );
            //       const productCatalogPrices = catalogEntries
            //         .filter(
            //           (sp) =>
            //             Number(sp.serviceProviderId) === spId &&
            //             sp.priceType === "GENERAL"
            //         )
            //         .find(
            //           (generalCatalogPrice) =>
            //             generalCatalogPrice?.general &&
            //             generalCatalogPrice?.general?.find(
            //               (general) => general.productId === poseProduct?.id
            //             )
            //         );
            //       const poseProductCatalogPrice =
            //         productCatalogPrices?.general?.find(
            //           (general) => general.productId === poseProduct?.id
            //         );
            //       return {
            //         ...productItem,
            //         productId: poseProduct?.id ?? productItem.productId,
            //         productName: `Transport Benne : Pose - ${volume}`,
            //         priceValue: poseProductCatalogPrice?.priceValue ?? "",
            //         comment: poseProductCatalogPrice?.comment ?? "",
            //         quantity: 1,
            //         isTransport:
            //           poseProduct?.isTransport ?? productItem.isTransport,
            //       };
            //     }
            //     return productItem;
            //   }),
            //   ...additionalProducts,
            // ];

            // Map zones with their names
            const zones = [...zoneIds].map((id) => {
              const catalogPriceZone = catalogEntries.find(
                (sp) => sp.zoneId === id
              );
              return {
                zoneId: id,
                zoneName: catalogPriceZone?.zoneName || "",
                distanceByCar: catalogPriceZone?.distanceByCar || "",
              };
            });

            // Return the final result
            return {
              serviceProviderId: spId,
              serviceProviderName,
              isValid: catalogPriceForSP?.isValid,
              formattedAddress: catalogPriceForSP?.formattedAddress || "",
              products: productsList,
              zones,
            };
          })
        );

      return {
        catalogPriceResult: result,
        selectedProducts: selectedProductsFromOrderClient,
      };
    } catch (error) {
      console.error("useCatalogPriceZoneServiceProvider error:", error);
      return [];
    }
  };

  return useRequest<{
    catalogPriceResult: CombineCatalogPriceLineZoneServiceProvider[];
    selectedProducts: SelectedProductsAndSellingPrices[];
  }>({
    action,
    params: query,
    default: [],
  });
};

export type CatalogPriceZonesSPType = {
  general?: CatalogPriceZoneSP[];
  forfait?: CatalogPriceZoneSP[];
  semiforfait?: CatalogPriceZoneSP[];
} | null;

/**
 * Map the catalog price zone service providers for price families.
 * @param params - The parameters of the mapping.
 * @param params.serviceProviderId - The service provider ID.
 * @param params.productId - The product ID.
 * @param params.priceFamilies - The list of price families.
 * @param params.catalogPriceZoneSP - The list of catalog price zone service providers.
 * @returns The list of price families mapped with their prices.
 */
export const mappingCatalogPriceZoneSPForPriceFamilies = (params: {
  serviceProviderId: number;
  productId?: number;
  priceFamilies: PriceFamily[];
  catalogPriceZoneSP?: CatalogPriceZonesSPType;
  manualProductPrices?: ManualProductPrices[];
}): CatalogPriceZoneSPPriceFamily[] => {
  // Destructure params
  const {
    serviceProviderId,
    productId,
    priceFamilies,
    catalogPriceZoneSP,
    manualProductPrices = [],
  } = params;

  // Map price families
  return priceFamilies.map((pf) => {
    const matchingPriceFamily = pf;

    // Get the correct tarif types based on the price type
    const correctTarifList = getCorrectDataTarifTypes(
      matchingPriceFamily.key,
      catalogPriceZoneSP
    );

    // find the catalog entry for the matching price family
    const catalogEntryForPriceFamily = correctTarifList.find(
      (e) =>
        (e.productId === productId || e.destProductId === productId) &&
        Number(e.serviceProviderId) === serviceProviderId &&
        ((e.priceFamilyId === matchingPriceFamily.id &&
          e.priceId === null &&
          e.priceOptionId === null) ||
          (e.DestCatalogPrice?.priceFamilyId === matchingPriceFamily.id &&
            e.DestCatalogPrice?.priceId === null &&
            e.DestCatalogPrice?.priceOptionId === null))
    );

    // Map price families with their prices
    const prices = (matchingPriceFamily.Prices || []).map((price) => {
      // find the catalog entry for the matching price
      const catalogEntryForPrice = correctTarifList.find(
        (e) =>
          (e.productId === productId || e.destProductId === productId) &&
          Number(e.serviceProviderId) === serviceProviderId &&
          ((e.priceFamilyId === matchingPriceFamily.id &&
            e.priceId === price.id &&
            e.priceOptionId === null) ||
            (e.DestCatalogPrice?.priceFamilyId === matchingPriceFamily.id &&
              e.DestCatalogPrice?.priceId === price.id &&
              e.DestCatalogPrice?.priceOptionId === null))
      );

      // Map price options
      const priceOptions: CatalogPriceZoneSPPriceOption[] = (
        price.PriceOptions || []
      ).map((po) => {
        // find the catalog entry for the matching price
        const catalogEntryForPriceOption = correctTarifList.find(
          (e) =>
            (e.productId === productId || e.destProductId === productId) &&
            Number(e.serviceProviderId) === serviceProviderId &&
            ((e.priceFamilyId === matchingPriceFamily.id &&
              e.priceId === price.id &&
              e.priceOptionId === po.id) ||
              (e.DestCatalogPrice?.priceFamilyId === matchingPriceFamily.id &&
                e.DestCatalogPrice?.priceId === price.id &&
                e.DestCatalogPrice?.priceOptionId === po.id))
        );

        const catalogPriceValue =
          catalogEntryForPriceOption?.priceValue ?? po?.defaultValue ?? "";

        // Map price option with its value
        return {
          ...po,
          priceValue: catalogPriceValue,
          catalogPriceValue,
          isManualPrice: false,
        };
      });

      const manualPriceEntry = price.manualOptionPrices?.find(
        (mop) =>
          mop.serviceProviderId === serviceProviderId &&
          mop.productId === productId
      );
      const manualPrice = manualPriceEntry?.manualPrice;
      const catalogPriceValue =
        catalogEntryForPrice?.priceValue ??
        price?.defaultPrice?.toString() ??
        "";
      const finalPriceValue = manualPrice?.toString() ?? catalogPriceValue;
      const isManual = !!manualPriceEntry || (catalogPriceValue !== "" && Number(finalPriceValue) !== Number(catalogPriceValue));

      // Map price with its value
      return {
        ...price,
        priceValue: finalPriceValue,
        catalogPriceValue,
        isManualPrice: isManual,
        priceOptions,
        ...(manualPriceEntry?.updatedAt && {
          orderCreatedAt: new Date(manualPriceEntry.updatedAt).toISOString(),
        }),
        ...(!manualPriceEntry?.updatedAt &&
          catalogEntryForPrice?.orderCreatedAt && {
          orderCreatedAt: catalogEntryForPrice.orderCreatedAt,
        }),
      };
    });

    const catalogPriceValue = catalogEntryForPriceFamily?.priceValue ?? "";

    // Find existing manual override for the family
    const manualFamilyOverride = (manualProductPrices || []).find(
      (m) =>
        m.productId === productId &&
        m.serviceProviderId === serviceProviderId &&
        m.priceFamilyId === matchingPriceFamily.id
    );
    const priceFamilyValue = manualFamilyOverride?.price ?? catalogPriceValue;
    const wasInitiallyManualFamily = !!manualFamilyOverride;

    const isManualPriceFamily =
      wasInitiallyManualFamily ||
      (catalogPriceValue !== "" &&
        Number(priceFamilyValue) !== Number(catalogPriceValue));

    return {
      ...matchingPriceFamily,
      serviceProviderId,
      priceValue: priceFamilyValue,
      catalogPriceValue,
      isManualPrice: isManualPriceFamily,
      wasInitiallyManual: wasInitiallyManualFamily,
      comment: catalogEntryForPriceFamily?.comment ?? "",
      prices,
      orderCreatedAt: manualFamilyOverride?.updatedAt
        ? new Date(manualFamilyOverride.updatedAt).toISOString()
        : catalogEntryForPriceFamily?.orderCreatedAt || null,
    };
  });
};

/**
 * Get the correct tarif types based on the price type.
 * @param priceType - The price type.
 * @param catalogPriceZoneSP - The catalog price zone service providers.
 * @returns The correct tarif types.
 */
export const getCorrectDataTarifTypes = (
  priceType?: ProductTarifType | null,
  catalogPriceZoneSP?: CatalogPriceZonesSPType
): CatalogPriceZoneSP[] => {
  /**
   * Switch on the price type to get the correct tarif types.
   * - GENERAL: return the general prices.
   * - FORFAIT: return the forfait prices.
   * - SEMIFORFAIT: return the semiforfait prices.
   * - default: return an empty array.
   */
  switch (priceType) {
    case TARIF_TYPE.GENERAL:
      return catalogPriceZoneSP?.general ?? [];
    case TARIF_TYPE.FORFAIT:
      return catalogPriceZoneSP?.forfait ?? [];
    case TARIF_TYPE.SEMIFORFAIT:
    case TARIF_TYPE.SEMIFORFAIT_NON_TRANSFORM:
      return catalogPriceZoneSP?.semiforfait ?? [];
    default:
      return [];
  }
};

export type SelectedProductsAndSellingPrices = {
  productId: number;
  productName: string;
  sellingUnitPrice: number;
  documentProductLineId: number;
  isBenne: boolean;
  isTransport: boolean;
};

/**
 * Retrieves selected products and their selling prices from an order client.
 * @param params - The input parameters including input products, product map, product data, and price family data.
 * @returns An array of objects containing productId, productName, and sellingUnitPrice.
 */
const getSelectedProductsAndSellingPricesFromOrderClient = (params: {
  inputProducts: InputProductType[];
  productMap: {
    productId: number;
    priceFamilyId: number | undefined;
    quantity: number;
    type: ProductTarifType;
    documentProductLineId: number;
  }[];
  productsData: Product[];
  priceFamiliesData: PriceFamily[];
}): SelectedProductsAndSellingPrices[] => {
  const { inputProducts, productMap, productsData } = params;

  // Prepare selling unit price list corresponding to products list
  const sellingUnitPriceList = inputProducts
    .map((p) => {
      // // Find the price family associated with the input product
      // const priceFamily = priceFamiliesData?.find(
      //   (pf) => pf.id === p?.priceFamilyId
      // );
      // // Find the product in the product map
      // const product = productMap?.find(
      //   (prod) => prod.productId === p?.productId
      // );

      // Check if the price family is semiforfait and the product type is not semiforfait non transform
      // if (
      //   priceFamily?.key === TARIF_TYPE.SEMIFORFAIT &&
      //   product?.type !== TARIF_TYPE.SEMIFORFAIT_NON_TRANSFORM
      // ) {
      //   let unitPriceList: {
      //     sellingPrice: number;
      //     destSellingPrice: number;
      //   }[] = [];
      //   // Filter the document product line prices based on conditions
      //   const documentProductLinePriceList =
      //     p?.DocumentProductLinePrices?.filter(
      //       (price) =>
      //         !(price.priceId && price.priceOptionId) ||
      //         (price?.priceId && !price?.priceOptionId)
      //     );

      //   // If there are valid document product line prices, calculate unit prices
      //   if (
      //     documentProductLinePriceList &&
      //     documentProductLinePriceList.length > 0
      //   ) {
      //     documentProductLinePriceList.forEach(
      //       (documentProductLinePriceOption) => {
      //         // Convert the price value to a number and add it to the list
      //         const unitPriceNumber = parseFloat(
      //           documentProductLinePriceOption?.value?.toString() ?? "0"
      //         );
      //         unitPriceList = [
      //           ...unitPriceList,
      //           {
      //             sellingPrice: unitPriceNumber,
      //             destSellingPrice:
      //               parseFloat(p?.unitPrice?.toString() ?? "0") ?? 0,
      //           },
      //         ];
      //       }
      //     );
      //     return unitPriceList;
      //   }
      // }

      // Return the unit price of the input product if no conditions are met
      return [
        {
          sellingPrice: parseFloat(p?.unitPrice?.toString() ?? "0"),
          destSellingPrice: 0,
        },
      ];
    })
    .flat();

  // Map each product in the product map with its corresponding selling unit price
  const mappedSellingPriceWithProducts = productMap.map((product, index) => {
    // Find the product data by product ID
    const productData = productsData?.find(
      (prod) => prod.id === product.productId
    );
    let sellingUnitPrice = sellingUnitPriceList[index]
      ? sellingUnitPriceList[index].sellingPrice
      : 0;
    // If the product name contains "Transport Benne : Pose et enlèvement - "
    // if (
    //   /Transport Benne : Pose et enlèvement - .+/.test(
    //     productData?.name || ""
    //   ) &&
    //   !inputProducts.find((p) => p.productId === productData?.id)
    // ) {
    //   // Set the selling unit price to the unit price of the destination product
    //   sellingUnitPrice = sellingUnitPriceList[index]
    //     ? sellingUnitPriceList[index].destSellingPrice
    //     : 0;
    // }

    return {
      productId: product.productId,
      productName: productData?.name || "",
      sellingUnitPrice,
      documentProductLineId: product.documentProductLineId,
      isBenne:
        productData?.ProductType?.ProductTypeIntervention?.key === "BENNE",
      isTransport: productData?.isTransport || false,
    };
  });

  return mappedSellingPriceWithProducts;
};

const handleLoadDefaultDataForSpOutsideZone = async (
  inputProducts: InputProductType[],
  productIds: Set<number>,
  qboServiceProviderId: number | null,
  contactId?: number,
  siteAddressFull?: string
) => {
  // Fetch data of products and price families
  const { productsData, priceFamiliesData } =
    await fetchProductsAndFamilies(
      productIds,
      qboServiceProviderId,
      contactId,
      siteAddressFull
    );

  // Preparing the quantity number list for source products by ordering
  const qtyListForProducts = inputProducts
    .map((p) => {
      // If the price family is not semiforfait
      return [parseFloat(p?.quantity?.toString() ?? "0") ?? 0];
    })
    .flat();

  // Fetch data of service providers
  const { data: providersData } = await boService.getQBOServiceProvidersByIds({
    "id[]": JSON.stringify([qboServiceProviderId]),
    limit: "unlimited",
  });

  const { data: manualData } = await boService.getQBOCatalogPriceZoneServiceProviders({
    serviceProviderZoneIdFulls: [],
    serviceProviderZoneIds: [],
    products: Array.from(productIds).map((id) => ({ productId: id })),
    contactId,
    siteAddressFull,
  });
  const manualProductPrices = manualData?.manualProductPrices || [];

  const result: CombineCatalogPriceLineZoneServiceProvider[] =
    providersData.rows.map((serviceProvider) => {
      let productsList = inputProducts.map((p, index) => {
        // Find the product
        const product = productsData?.rows.find(
          (prod) => prod.id === p.productId
        );

        // Handles for the case - tarif details
        const selectedPriceFamilyId = p?.priceFamilyId;
        // Find the price families
        const filteredPriceFamilies = priceFamiliesData.filter(
          (pf) => pf.productTypeId === product?.productTypeId
        );

        // Map product with its price families
        const priceFamilies = mappingCatalogPriceZoneSPForPriceFamilies({
          serviceProviderId: serviceProvider.id,
          productId: p.productId,
          priceFamilies: filteredPriceFamilies,
          manualProductPrices,
        });

        // Initialize mapping components
        let priceValue = "";
        let isManualPrice = false;
        let wasInitiallyManual = false;
        let orderCreatedAt: string | null = null;
        let comment = "";

        // Check for manual price in database at product level
        const manualPriceAtProductLevel = manualProductPrices.find(
          (manual) => manual.productId === p.productId && manual.serviceProviderId === serviceProvider.id
        );

        if (manualPriceAtProductLevel) {
          isManualPrice = true;
          wasInitiallyManual = true;
          if (manualPriceAtProductLevel.updatedAt) {
            orderCreatedAt = new Date(manualPriceAtProductLevel.updatedAt).toISOString();
          }
          if (manualPriceAtProductLevel.price) {
            priceValue = manualPriceAtProductLevel.price;
          }
        }

        // Find the selected price family
        const selectedPriceFamily = priceFamilies.find((pf) => pf.id === selectedPriceFamilyId);

        if (selectedPriceFamily) {
          priceValue = selectedPriceFamily?.priceValue ?? "";
          comment = selectedPriceFamily?.comment ?? "";
          if (selectedPriceFamily.isManualPrice) {
            isManualPrice = true;
          }
          if (selectedPriceFamily.wasInitiallyManual) {
            wasInitiallyManual = true;
          }
          if (selectedPriceFamily.orderCreatedAt) {
            orderCreatedAt = selectedPriceFamily.orderCreatedAt;
          }
        } else {
          const manualPriceAtFamilyLevel = manualProductPrices.find(
            (manual) =>
              manual.productId === p.productId &&
              manual.serviceProviderId === serviceProvider.id &&
              manual.priceFamilyId === selectedPriceFamilyId
          );
          if (manualPriceAtFamilyLevel) {
            priceValue = manualPriceAtFamilyLevel?.price ?? "";
            isManualPrice = true;
            wasInitiallyManual = true;
            if (manualPriceAtFamilyLevel.updatedAt) {
              orderCreatedAt = new Date(manualPriceAtFamilyLevel.updatedAt).toISOString();
            }
          }
        }

        const enlevementProduct = /Transport Benne : Enlèvement - .+/.test(
          product?.name || ""
        );
        const rotationProduct = /Transport Benne : Rotation - .+/.test(
          product?.name || ""
        );
        // Map product with its price families
        return {
          productId: product?.id,
          productName: product?.name ?? "",
          priceFamilies,
          priceValue,
          catalogPriceValue: "",
          isManualPrice,
          wasInitiallyManual,
          selectedPriceFamilyId,
          quantity: product?.isTransport
            ? enlevementProduct || rotationProduct
              ? 0
              : 1
            : qtyListForProducts[index],
          isTransport: product?.isTransport,
          comment,
          productTypeInterventionKey:
            product?.ProductType?.ProductTypeIntervention?.key,
          // sellingUnitPrice: parseFloat(
          //   inputProducts[index]?.unitPrice?.toString() ?? "0"
          // ),
          documentProductLineId: p.documentProductLineId,
          documentProductLinePrices: p?.SPDocumentProductLinePrices,
          orderCreatedAt,
        };
      });

      return {
        serviceProviderId: serviceProvider.id,
        serviceProviderName: serviceProvider.name,
        formattedAddress: serviceProvider.formattedAddress || "",
        products: productsList,
        zones: [],
      };
    });

  const selectedProductsFromOrderClient = productsData.rows.map(
    (product, index) => {
      const inputProduct = inputProducts.find(
        (p) => p.productId === product.id
      );
      const sellingUnitPrice = parseFloat(
        inputProduct?.unitPrice?.toString() ?? "0"
      );
      return {
        productId: product.id,
        productName: product?.name || "",
        sellingUnitPrice,
        documentProductLineId: inputProduct?.documentProductLineId,
        isBenne: product?.ProductType?.ProductTypeIntervention?.key === "BENNE",
        isTransport: product?.isTransport || false,
      };
    }
  );

  return {
    catalogPriceResult: result,
    selectedProducts: selectedProductsFromOrderClient,
  };
};

/**
 * Fetches products and their price families from the API.
 * @param productIds - A set of product IDs to fetch.
 * @returns A promise resolving to an object containing the products and their price families.
 */
const fetchProductsAndFamilies = async (
  productIds: Set<number>,
  serviceProviderId?: number | null,
  contactId?: number,
  siteAddressFull?: string
) => {
  const productIdsArray = [...productIds];

  // Fetch the products
  const { data: productsDataResponse } = await boService.getQBOProductsByIds({
    // The API expects an array of product IDs as a string
    "id[]": JSON.stringify(productIdsArray),
    limit: "unlimited",
    include: "ProductType.ProductTypeIntervention",
  });

  // Get the product type IDs from the fetched products
  const productTypeIds = productsDataResponse?.rows.map((p) => p.productTypeId);

  // Fetch the price families for the product types
  const { data: priceFamiliesData } = await boService.getQBOPriceFamilies({
    // The API expects an array of product type IDs as a string
    "productTypeId[]": JSON.stringify(productTypeIds),
    // Include the prices with their options and type logic
    include: "Prices.PriceOptions|Prices.PriceTypeLogic",
    ...(serviceProviderId ? { serviceProviderId } : {}),
    ...(contactId ? { contactId } : {}),
    ...(siteAddressFull ? { siteAddressFull } : {}),
    ...(productsDataResponse?.rows?.[0]?.id
      ? { productId: productsDataResponse.rows[0].id }
      : {}),
  });

  return { productsData: productsDataResponse, priceFamiliesData };
};
